/**
 * Owner, 24.10.2026: «настройки инвертора отображать у нас» — the installer's
 * limits as the schedule takes them (the latest read, read again before each
 * hourly plan; else the original settings): Max A Charge / Discharge, the
 * Grid Charge switch, the grid charge current (the program's for a grid
 * charge window, the installer's beside it), the battery voltage, and the
 * schedule's kW within them. Phage never changes the limits — only the
 * windows, the work mode and the grid charge current.
 */
import type { SiteResponse } from 'shared';

import { useLocale } from '../i18n/locale-context.js';
import { fmtDateTime, fmtNumShort } from './format.js';
import { limitsPlan } from './inverter-program.js';

export function InverterLimits({ site }: { site: SiteResponse }) {
  const { t, tf, locale } = useLocale();
  const l = site.inverter_settings.limits;
  const num = (v: number | null, digits = 1) => (v === null ? '—' : fmtNumShort(locale, v, digits));
  const plan = limitsPlan(l);
  const gridA =
    l.grid_charge_a !== null &&
    l.grid_charge_a_original !== null &&
    l.grid_charge_a !== l.grid_charge_a_original
      ? tf('site.lim.grid_a.program', {
          now: num(l.grid_charge_a, 0),
          original: num(l.grid_charge_a_original, 0),
        })
      : num(l.grid_charge_a ?? l.grid_charge_a_original, 0);
  return (
    <div className="inv-prog" data-testid="inverter-limits">
      <div className="chart-title">{t('site.lim.title')}</div>
      <p className="sub" style={{ margin: '0 0 10px' }}>
        {l.source && l.at
          ? tf(l.source === 'read' ? 'site.lim.read' : 'site.lim.original', {
              at: fmtDateTime(l.at, site.timezone),
            })
          : t('site.lim.none')}
      </p>
      <dl className="kv2 lim">
        <dt>{t('site.lim.charge')}</dt>
        <dd className="num">{num(l.charge_a, 0)}</dd>
        <dt>{t('site.lim.discharge')}</dt>
        <dd className="num">{num(l.discharge_a, 0)}</dd>
        <dt>{t('site.lim.grid_switch')}</dt>
        <dd>
          {l.grid_charge_on === null
            ? t('site.orig.unknown')
            : t(l.grid_charge_on ? 'site.orig.tou.on' : 'site.orig.tou.off')}
        </dd>
        <dt>{t('site.lim.grid_a')}</dt>
        <dd className="num">{gridA}</dd>
        <dt>{t('site.lim.volts')}</dt>
        <dd className="num">{num(l.battery_v)}</dd>
        <dt>{t('site.lim.rated')}</dt>
        <dd className="num">{num(l.rated_kw)}</dd>
        <dt>{t('site.lim.plan')}</dt>
        <dd>
          {plan.gridKw > 0
            ? tf('site.lim.plan.value', { power: num(plan.powerKw), grid: num(plan.gridKw) })
            : tf('site.lim.plan.no_grid', { power: num(plan.powerKw) })}
        </dd>
      </dl>
      <p className="sub" style={{ margin: '8px 0 0' }}>
        {t('site.lim.changes')}
      </p>
    </div>
  );
}
